import { Marker, Tooltip } from 'react-leaflet';
import { DivIcon } from 'leaflet';
import { useTheme } from '../../hooks/useTheme';
import { HomeBase } from '../../types/homeBase';
import { formatLatLng } from '../../utils/formatters';

interface HomeBaseMarkerProps {
  homeBase: HomeBase;
}

/**
 * Map marker for a drone's home base.
 *
 * Uses a house-shaped SVG icon (never colour alone) so the marker is
 * distinguishable from the round drone markers even in greyscale or for
 * colour-blind operators. Colour: #0066FF (contrast 5.74:1 on white).
 */
export const HomeBaseMarker: React.FC<HomeBaseMarkerProps> = ({ homeBase }) => {
  const { isDarkMode } = useTheme();

  if (!homeBase.is_set) {
    return null;
  }

  const bgColor = isDarkMode ? '#2d2d2d' : '#ffffff';
  const textColor = isDarkMode ? '#e0e0e0' : '#212121';

  const iconHtml = `
    <div
      role="img"
      aria-label="Home base location"
      title="Home base"
      style="
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
      "
    >
      <div style="
        background: ${bgColor};
        border: 2.5px solid #0066FF;
        border-radius: 6px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.35);
      ">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="#0066FF"
          aria-hidden="true"
        >
          <!-- House shape: roof + walls + door -->
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
        </svg>
      </div>
      <div style="
        background: ${bgColor};
        color: ${textColor};
        padding: 2px 5px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: bold;
        margin-top: 3px;
        box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        white-space: nowrap;
        border: 1px solid #0066FF;
      ">
        Home
      </div>
    </div>
  `;

  const icon = new DivIcon({
    html: iconHtml,
    className: 'home-base-marker',
    iconSize: [60, 70],
    iconAnchor: [30, 55],
  });

  return (
    <Marker position={[homeBase.latitude, homeBase.longitude]} icon={icon}>
      <Tooltip direction="top" offset={[0, -55]}>
        <div>
          <strong>Home Base</strong>
          <br />
          {formatLatLng(homeBase.latitude, homeBase.longitude)}
          {homeBase.altitude !== 0 && (
            <>
              <br />
              Altitude: {homeBase.altitude.toFixed(1)} m
            </>
          )}
        </div>
      </Tooltip>
    </Marker>
  );
};
